import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resetAgentQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/valueByDateHelpers';
import { resetEnvelopeQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/apiHelpers';
import {
    RQAPICallWithData,
    RQAPICall,
    HttpResponse,
    HttpError,
} from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { PayComponentMapping, WageTypeSource } from '../helpers/definitions';

export const useGetPayComponentMappingsData = () =>
    useQuery({
        queryKey: ['pay_component_mappings'],

        queryFn: async () => {
            const response = await RQAPICallWithData<PayComponentMapping[]>({
                endpoint: 'mappings/pay_components',
            });
            return response.content.data;
        },
    });

export const useGetWageTypeSourcesData = () =>
    useQuery({
        queryKey: ['wage_type_sources'],

        queryFn: async () => {
            const response = await RQAPICallWithData<WageTypeSource[]>({
                endpoint: 'mappings/wage_type_sources',
            });
            return response.content.data;
        },
    });

type SourcesMutationProps = { destinationId: number; sourceIds: number[] };

export const useUpdatePayComponentSources = (shouldResetQuery?: boolean) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation<HttpResponse<object>, Error, SourcesMutationProps>({
        mutationFn: async ({
            sourceIds,
            destinationId,
        }: {
            sourceIds: number[];
            destinationId: number;
        }) => {
            const response = await RQAPICall({
                method: 'PUT',
                endpoint: `mappings/destination/wage_type.pay_component/${destinationId} `,
                body: { sourceIds },
            });
            return response;
        },

        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: ['wage_type_sources'],
            });
            queryClient.invalidateQueries({
                queryKey: ['pay_component_mappings'],
            });
            if (shouldResetQuery) {
                queryClient.resetQueries({
                    queryKey: ['pay_component_mappings'],
                });
                queryClient.resetQueries({ queryKey: ['wage_type_sources'] });
            }

            // PayComponent Params have a direct impact on envelop and agent result.
            //  We want to reset ther query cache once the params are changed.
            resetEnvelopeQueries(queryClient);
            resetAgentQueries(queryClient);
            dispatch.vision.notifyUser({
                message: response.content.message || '',
                snackType: 'success',
            });
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};
type DestinationsMutationProps = {
    destinationId: number | null;
    sourceId: number;
};

export const useUpdatePayComponentDestination = (
    shouldResetQuery?: boolean,
) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation<HttpResponse<object>, Error, DestinationsMutationProps>({
        mutationFn: async ({
            sourceId,
            destinationId,
        }: {
            sourceId: number;
            destinationId: number | null;
        }) => {
            const response = await RQAPICall({
                method: 'PUT',
                endpoint: `mappings/source/wage_type.pay_component/${sourceId} `,
                body: { destinationId },
            });
            return response;
        },

        onSuccess: (response: HttpResponse<object>) => {
            queryClient.invalidateQueries({
                queryKey: ['wage_type_sources'],
            });
            queryClient.invalidateQueries({
                queryKey: ['pay_component_mappings'],
            });

            if (shouldResetQuery) {
                queryClient.resetQueries({
                    queryKey: ['pay_component_mappings'],
                });
                queryClient.resetQueries({
                    queryKey: ['wage_type_sources'],
                });
            }
            // PayComponent Params have a direct impact on envelop and agent result.
            //  We want to reset ther query cache once the params are changed.
            resetEnvelopeQueries(queryClient);
            resetAgentQueries(queryClient);
            dispatch.vision.notifyUser({
                message: response.content.message || '',
                snackType: 'success',
            });
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};
